import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, useWatch } from 'react-hook-form'
import dayjs from 'dayjs'
import * as api from '@/apis/aipass'
import { Checkbox } from '@/components/atoms/checkbox'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { TextareaField } from '@/components/molecules/textarea-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { AdminGuestRoomResponseType } from '@/models/admin-guest-room'
import { BulkOperationPeriodType, PREPARATION_MEMO_MAX_LENGTH, countBulkOperationPeriodDays } from '@/models/room-manager/bulk-operation'
import { PreparationType } from '@/models/room-manager/response/guest-room-indicator'
import { BulkOperationModal } from './bulk-operation-modal'
import { BulkOperationField, BulkOperationNote } from './bulk-operation-field'
import { BulkOperationPeriodField } from './bulk-operation-period-field'

type PreparationBulkCreateModalProps = {
  hotelGuestRooms: AdminGuestRoomResponseType['guestRoomInfo']
  onCancel: () => void
  onCompleted: () => void
}

export type PreparationBulkCreateFormValue = {
  period: BulkOperationPeriodType
  preparationType: PreparationType
  roomTypeIds: string[]
  preparationMemo: string
}

export const PreparationBulkCreateModal: React.FC<PreparationBulkCreateModalProps> = ({ hotelGuestRooms, onCancel, onCompleted }) => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const { errorHandler } = useErrorHandler()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<PreparationBulkCreateFormValue>({
    mode: 'all',
    defaultValues: {
      period: {
        beginDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().add(6, 'day').format('YYYY-MM-DD'),
      },
      preparationType: PreparationType.OutOfOrder,
      roomTypeIds: [],
      preparationMemo: '',
    },
  })
  const watchPeriod = useWatch({ control, name: 'period' })
  const watchPreparationType = useWatch({ control, name: 'preparationType' })
  const watchRoomTypeIds = useWatch({ control, name: 'roomTypeIds' })

  const roomTypes = useMemo(
    () =>
      hotelGuestRooms.map(guestRoom => ({
        roomTypeId: guestRoom.roomTypeId,
        typeName: pickByLanguage({ ja: guestRoom.typeNameJa, en: guestRoom.typeNameEn }),
        roomCount: Object.values(guestRoom.roomNumber ?? {}).reduce((count, rooms) => count + rooms.length, 0),
      })),
    [hotelGuestRooms],
  )

  const selectedRoomCount = useMemo(
    () => roomTypes.filter(type => watchRoomTypeIds.includes(type.roomTypeId)).reduce((count, type) => count + type.roomCount, 0),
    [roomTypes, watchRoomTypeIds],
  )

  const dayCount = countBulkOperationPeriodDays(watchPeriod)

  const onSubmit = async ({ period, preparationType, roomTypeIds, preparationMemo }: PreparationBulkCreateFormValue) => {
    try {
      setIsSubmitting(true)
      await api.bulkCreateGuestRoomPreparation({ ...period, preparationType, roomTypeIds, preparationMemo })
      onCompleted()
    } catch (e) {
      // 失敗時はモーダルを開いたままにして、条件を変えず再実行できるようにする
      errorHandler(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <BulkOperationModal
      title={t('roomManagerBulk.PreparationTitle')}
      description={t('roomManagerBulk.PreparationDescription')}
      summary={
        selectedRoomCount > 0 && dayCount > 0
          ? t('roomManagerBulk.PreparationSummary', {
              rooms: selectedRoomCount,
              days: dayCount,
              total: selectedRoomCount * dayCount,
            })
          : undefined
      }
      submitLabel={t('roomManagerBulk.PreparationSubmit')}
      isSubmittable={isValid}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
    >
      <BulkOperationPeriodField control={control} disabled={isSubmitting} />

      <BulkOperationField label={t('roomManagerBulk.PreparationTypeLabel')} required>
        <Controller
          control={control}
          name="preparationType"
          render={({ field: { onChange, value } }) => (
            <RadioGroupField
              value={String(value)}
              name="bulk-preparation-type"
              disabled={isSubmitting}
              onChange={e => onChange(Number(e.currentTarget.value))}
              items={[
                { value: String(PreparationType.OutOfOrder), label: t('roomManagerBulk.PreparationTypeOoo') },
                { value: String(PreparationType.OutOfService), label: t('roomManagerBulk.PreparationTypeOos') },
              ]}
              groupCss={css({ gap: 24 })}
              style={{ radioSize: 18, labelMargin: 5 }}
            />
          )}
        />
        <p css={typeDescriptionStyle}>
          {t(
            watchPreparationType === PreparationType.OutOfOrder
              ? 'roomManagerBulk.PreparationTypeOooHint'
              : 'roomManagerBulk.PreparationTypeOosHint',
          )}
        </p>
      </BulkOperationField>

      <BulkOperationField
        label={t('roomManagerBulk.RoomTypeLabel')}
        required
        note={t('roomManagerBulk.RoomTypeSelectedCount', {
          selected: watchRoomTypeIds.length,
          total: roomTypes.length,
          rooms: selectedRoomCount,
        })}
      >
        <Controller
          control={control}
          name="roomTypeIds"
          rules={{ validate: value => value.length > 0 || t('Required field has not been entered') }}
          render={({ field: { onChange, value } }) => (
            <div css={roomTypeListStyle}>
              <div className="head">
                <Checkbox
                  value={value.length === roomTypes.length && roomTypes.length > 0}
                  partial={value.length > 0 && value.length < roomTypes.length}
                  disabled={isSubmitting || roomTypes.length === 0}
                  label={t('Select all')}
                  onChange={checked => onChange(checked ? roomTypes.map(type => type.roomTypeId) : [])}
                />
              </div>
              <div className="rows">
                {roomTypes.map(roomType => (
                  <div className="row" key={roomType.roomTypeId}>
                    <Checkbox
                      value={value.includes(roomType.roomTypeId)}
                      disabled={isSubmitting}
                      label={roomType.typeName}
                      onChange={checked =>
                        onChange(checked ? [...value, roomType.roomTypeId] : value.filter(id => id !== roomType.roomTypeId))
                      }
                    />
                    <span className="count">{t('roomManagerBulk.RoomCount', { rooms: roomType.roomCount })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        />
      </BulkOperationField>

      <BulkOperationField label={t('roomManagerBulk.PreparationMemoLabel')}>
        <Controller
          control={control}
          name="preparationMemo"
          render={({ field: { onChange, value } }) => (
            <>
              <TextareaField
                value={value}
                disabled={isSubmitting}
                maxLength={PREPARATION_MEMO_MAX_LENGTH}
                placeholder={t('roomManagerBulk.PreparationMemoPlaceholder')}
                marginBottom={4}
                handleChangeData={e => onChange(e.target.value)}
              />
              <p css={memoCountStyle}>{`${value.length} / ${PREPARATION_MEMO_MAX_LENGTH}`}</p>
            </>
          )}
        />
      </BulkOperationField>

      <BulkOperationNote items={[t('roomManagerBulk.PreparationNoteSkip'), t('roomManagerBulk.PreparationNoteOooStock')]} tone="info" />
    </BulkOperationModal>
  )
}

const typeDescriptionStyle = css({
  marginTop: 8,
  fontSize: 11,
  lineHeight: 1.6,
  color: '#a3a3a3',
})

const roomTypeListStyle = css({
  border: '1px solid #e3e3e3',
  borderRadius: 4,
  overflow: 'hidden',
  '.head': {
    padding: '9px 12px',
    backgroundColor: '#f7f8fa',
    borderBottom: '1px solid #e3e3e3',
  },
  // 部屋タイプが多いホテルでもモーダル高さを一定に保つため、一覧側でスクロールさせる
  '.rows': {
    maxHeight: 152,
    overflowY: 'auto',
  },
  '.row': {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 12px',
    borderBottom: '1px solid #f2f2f2',
    ':last-of-type': { borderBottom: 'none' },
  },
  '.count': {
    marginLeft: 'auto',
    fontSize: 12,
    letterSpacing: 0.6,
    color: '#a3a3a3',
  },
})

const memoCountStyle = css({
  fontSize: 11,
  color: '#a3a3a3',
  textAlign: 'right',
})
